import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { min, max, isSameDay } from 'date-fns';

import { parseDate } from '../utils/dateHelper';
import { ILessonSearchProps, ILesson, IFilterEntry } from './types';
import t from './locales';
import { flatten, uniq, sleep } from '../utils/commonHelper';

import DatePicker from '../Calendar/DatePicker';
import Modal from '../Shared/Modal';
import { Field, FieldLabel } from '../Shared/Fields';
import Loading from '../Shared/Loading';

import ContainerView from './ContainerView';
import FilterContainerView from './FilterContainerView';
import LessonItem from './LessonItem';
import DateField from './DateField';
import InclusionFilter from './InclusionFilter';


const LessonSearch: React.FC<ILessonSearchProps> = (props) => {
  const { localCurrency } = props;
  const FILTER_ID_AVAILABILITY = 'availability';
  const FILTER_ID_RESORT_CODE = 'resortCode';
  const FILTER_ID_PARTICIPANT_COUNT = 'participantCount';
  const allLessons = props.lessons.map(lesson => (
    {
      ...lesson,
      [FILTER_ID_AVAILABILITY]: lesson[FILTER_ID_AVAILABILITY].map(dateString => parseDate(dateString)),
    }
  ));
  // Local states
  const allDate = uniq(flatten(allLessons.map(lesson => lesson.availability)));
  // In case we need search by resort
  const allResorts = uniq(flatten(allLessons.map(lesson => lesson.resorts)), 'resortCode');
  const [selectedDateList, setDateList] = useState<Date[]>([]); // for calendar
  const [currentLessons, setCurrentLessons] = useState<ILesson[]>(allLessons);
  const [filters, setFilters] = useState<IFilterEntry[]>([]);
  const [isModalShown, setModalShown] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const isSherpa = allLessons.some(item => item.sherpa);
  const participantOptions = useMemo(() => {
    const counts = allLessons
      .map(lesson => lesson.participantCount)
      .filter(Number.isFinite);

    const minCount = counts.length ? Math.min(...counts) : 0;
    const maxCount = counts.length ? Math.max(...counts) : 0;
    const options = [];
    for (let i = minCount; i <= maxCount; i++) {
      options.push(i);
    }

    return options;
  }, [allLessons]);

  function lessonsFilteredBy(filterEntry: IFilterEntry): Set<ILesson> {
    const { filterId, selectedValues } = filterEntry;
    let result: ILesson[] = [];
    if (filterId === FILTER_ID_AVAILABILITY) {
      result = allLessons.filter(lesson => (
        selectedValues.some(value => (
          lesson[FILTER_ID_AVAILABILITY].some(date => isSameDay(date, value))
        ))
      ));
    } else if (filterId === FILTER_ID_RESORT_CODE) {
      result = allLessons.filter(lesson => (
        selectedValues.some(value => (
          lesson.resorts.some(resort => resort.resortCode === value)
        ))
      ));
    } else if (filterId === FILTER_ID_PARTICIPANT_COUNT) {
      result = allLessons.filter(lesson => (
        selectedValues.some(value => (
          lesson.participantCount === Number(value)
        ))
      ));
    } else {
      result = allLessons.filter(lesson => (
        selectedValues.some(value => (
          lesson[filterId].indexOf(value) !== -1
        ))
      ));
    }
    return new Set(result);
  }

  useEffect(() => {
    if (filters.length > 0) {
      let result: ILesson[] = [...allLessons];
      filters.forEach((filterEntry: IFilterEntry) => {
        result = [...result].filter(lesson => lessonsFilteredBy(filterEntry).has(lesson));
      });
      setCurrentLessons(result);
    } else {
      setCurrentLessons(allLessons);
    }
  }, [filters]);

  function updateFilter(input: IFilterEntry) {
    const index = filters.findIndex(existing => existing.filterId === input.filterId);
    const isRemoval = input.selectedValues.every(value => value === '');
    if (isRemoval) {
      if (index === -1) {
        // Do nothing
      } else {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
      }
    } else {
      if (index === -1) {
        setFilters([...filters, input]);
      } else {
        const newFilter = [...filters];
        newFilter[index] = input;
        setFilters(newFilter);
      }
    }
  }

  async function handleDateListChange(selectedDateList: Date[]) {
    setLoading(true);
    await sleep();
    setDateList(selectedDateList);
    updateFilter({ filterId: FILTER_ID_AVAILABILITY, selectedValues: selectedDateList });
    setLoading(false);
  }

  async function handleSelectChange(filterEntry: IFilterEntry) {
    setLoading(true);
    await sleep();
    updateFilter(filterEntry);
    setLoading(false);
  }

  return (
    <div>
      <ContainerView>
        <FilterContainerView>
          <div className="header">{t.filterLessons()}</div>
          <div className="filters">
            {isSherpa ? (
              <div style={{ marginBottom: '1em' }}>
                <FieldLabel>{t.dateRange()}</FieldLabel>
                <Field>
                  <DateField
                    selectedDateList={selectedDateList}
                    handleClick={() => setModalShown(true)}
                  />
                </Field>
              </div>
            ) : (
              <div style={{ marginBottom: '1em' }}>
                <FieldLabel>{t.participantCount()}</FieldLabel>
                <InclusionFilter
                  handleSelectChange={handleSelectChange}
                  filterId="participantCount"
                >
                  <option value=""></option>
                  {participantOptions.map(count => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </InclusionFilter>
              </div>
            )}
            {/*
            <div style={{ marginBottom: '1em' }}>
              <FieldLabel>{t.skiResort()}</FieldLabel>
              <InclusionFilter handleSelectChange={handleSelectChange} filterId="resortCode">
                <option value=""></option>
                {allResorts.map((resort: IResort) => (
                  <option key={resort.resortCode} value={resort.resortCode}>{resort.resortName}</option>
                ))}
              </InclusionFilter>
            </div>
            */}
            <div style={{ marginBottom: '1em' }}>
              <FieldLabel>{t.language()}</FieldLabel>
              <InclusionFilter handleSelectChange={handleSelectChange} filterId="languages">
                <option value=""></option>
                <option value="english">{t.english()}</option>
                <option value="chinese">{t.chinese()}</option>
                <option value="japanese">{t.japanese()}</option>
              </InclusionFilter>
            </div>
            <div style={{ marginBottom: '1em' }}>
              <FieldLabel>{t.subject()}</FieldLabel>
              <InclusionFilter handleSelectChange={handleSelectChange} filterId="subjects">
                <option value=""></option>
                <option value="ski">{t.ski()}</option>
                <option value="snowboard">{t.snowboard()}</option>
              </InclusionFilter>
            </div>
            <div>
              <FieldLabel>{t.participantType()}</FieldLabel>
              <InclusionFilter handleSelectChange={handleSelectChange} filterId="participantTypes">
                <option value=""></option>
                <option value="private">{t.private()}</option>
                <option value="group">{t.group()}</option>
              </InclusionFilter>
            </div>
          </div>
        </FilterContainerView>
        <div>
          {currentLessons.length > 0 ?
            <p>{t.found({ count: currentLessons.length })}</p> :
            <p style={{ padding: '8px 0' }}>{t.notFound()}</p>}
          {isLoading ?
            <Loading isLoading={true} /> :
            currentLessons.map(lesson => (
              <LessonItem
                lesson={lesson}
                selectedDateList={selectedDateList}
                localCurrency={localCurrency}
                key={lesson.snowItemId} />
            ))
          }
        </div>
      </ContainerView>
      <Modal isShown={isModalShown} handleClose={() => { setModalShown(false); }}>
        <DatePicker
          handleDateListChange={handleDateListChange}
          minDate={min(allDate)}
          maxDate={max(allDate)}
          isRangeSelect={true}
          isMultiSelect={false}
        />
      </Modal>
    </div>
  );
};

export default LessonSearch
